import { Trans, t } from "@lingui/macro";
import { ChevronUp, ChevronDown, Loader2, CornerDownRight, SendHorizontal } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { Room } from "../../models/Room";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { Util } from "../../services/Utils/Util";
import { getDisplayName } from "../../services/Utils/userDisplayName";
import MessageItem, { MessageItemState, GetReactionSummary } from "../components/MessageItem";
import { UIMessage } from "../components/UIMessage";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { Message } from "../../models/Message";
import { IconChevronDown, IconSmilePlus } from "../components/FigmaIcons";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import MsgInputFooterReactions from "../components/message-inputs/MsgInputFooterReactions";
import MsgInputFooter from "../components/message-inputs/MsgInputFooter";
import { Soribot } from "../../models/Soribot";
import MentionTextArea from "../components/MentionTextArea";
import { SuggestionDataItem } from "react-mentions";
import MemoOpenModal from "./right-menu/MemoOpenModal";
import { MemoModalEmitter } from "./right-menu/PanelMemos";
import { RoomItem } from "../../models/RoomItem";

/** 서버 reactions 맵 기반 간이 리액션 요약 (자식 미로드 시 사용) */
const REACTION_ICONS: Record<string, string> = {
  'smile': '/assets/icon/ic-smile-24.svg',
  'heart': '/assets/icon/ic-heart-24.svg',
  'thumbs-up': '/assets/icon/ic-thumbs-up-24.svg',
  'shake-hand': '/assets/icon/ic-shake-hand-24.svg',
  'check': '/assets/icon/ic-check-24.svg',
};
const REACTION_EMOJI: Record<string, string> = { 'rocket': '🚀', 'party': '🎉' };

const ServerReactionSummary: React.FC<{ reactions?: { [emoticon: string]: string[] } }> = ({ reactions }) => {
  if (!reactions) return null;
  const entries = Object.entries(reactions).filter(([, users]) => users.length > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-lg mt-[3px] w-fit">
      {entries.map(([emoticon, users]) => (
        <div key={emoticon} className="flex items-center gap-1">
          {REACTION_ICONS[emoticon]
            ? <img src={REACTION_ICONS[emoticon]} className="w-5 h-5" />
            : <span>{REACTION_EMOJI[emoticon] ?? emoticon}</span>
          }
          <span className="text-sm">{users.length}</span>
        </div>
      ))}
    </div>
  );
};

/** 메시지 카드 형태 스켈레톤 (로딩 중 표시) */
const MessageSkeletonCard: React.FC<{ widths?: [string, string] }> = ({ widths = ['60%', '80%'] }) => (
  <div className="message-root px-4 mb-2">
    <div className="border border-border rounded-panel py-3.5 px-4">
      {/* 아바타 + 이름 + 시간 */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full skeleton-shimmer shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: '120px' }} />
          <div className="h-2.5 skeleton-shimmer rounded-full" style={{ width: '60px' }} />
        </div>
      </div>
      {/* 메시지 본문 */}
      <div className="flex flex-col gap-2 pl-12">
        <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: widths[0] }} />
        <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: widths[1] }} />
      </div>
    </div>
  </div>
);

const MessageSkeletonGroup: React.FC = () => (
  <div className="py-2">
    <MessageSkeletonCard widths={['70%', '90%']} />
    <MessageSkeletonCard widths={['50%', '75%']} />
    <MessageSkeletonCard widths={['85%', '60%']} />
    <MessageSkeletonCard widths={['40%', '70%']} />
    <MessageSkeletonCard widths={['65%', '45%']} />
  </div>
);

const MessageModal: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [room, setRoom] = useState<Room>();
  const [contextMessages, setContextMessages] = useState<UIMessage[]>([]);
  const { messageToShow, setMessageToShow } = useAppUIContext();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const [activeThreads, setActiveThreads] = useState<Set<string>>(new Set());
  const [activeReactionId, setActiveReactionId] = useState<string | null>(null);
  const [currentMessage, setCurrentMessage] = useState<UIMessage | null>(null);
  const [directMemoItem, setDirectMemoItem] = useState<RoomItem | null>(null);
  const soribotsRef = useRef<Soribot[]>([]);

  useEffect(() => {
    if (messageToShow) {
      setShowModal(true);
      setActiveThreads(new Set([messageToShow.parentMessageId ?? messageToShow.id]));
      initiate();
    } else {
      setShowModal(false);
      setContextMessages([]);
    }
  }, [messageToShow]);

  const toggleThread = useCallback(async (parentId: string) => {
    const parent = contextMessages.find(m => m.msg.id === parentId);
    const needsLoad = parent && parent.children.length === 0 && (parent.replyCount ?? 0) > 0;

    if (needsLoad) {
      // 자식 미로드 상태: 무조건 열림 유지 + 로드
      setActiveThreads(prev => {
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });

      try {
        const children = await Fire.msg.getMessagesByParentIds(My.user.id, [parentId]);
        const sortedChildren = children.sort((a, b) => a.created < b.created ? -1 : 1).map(m => new UIMessage(m, []));

        const finalChildren: UIMessage[] = [];
        sortedChildren.forEach(c => {
          if (c.msg.emoticon && c.msg.replyToMessageId) {
            const replyParent = finalChildren.find(a => a.msg.id === c.msg.replyToMessageId);
            if (replyParent) replyParent.children.push(c);
            else finalChildren.push(c);
          } else {
            finalChildren.push(c);
          }
        });

        setContextMessages(prev => prev.map(m => {
          if (m.msg.id === parentId) {
            const updated = new UIMessage(m.msg, finalChildren);
            updated.isNewDay = m.isNewDay;
            updated.replyCount = m.replyCount;
            return updated;
          }
          return m;
        }));
      } catch (e) {
        console.error('Failed to load thread children:', e);
      }
    } else {
      // 자식 이미 로드: 일반 토글
      setActiveThreads(prev => {
        const next = new Set(prev);
        if (next.has(parentId)) next.delete(parentId);
        else next.add(parentId);
        return next;
      });
    }
  }, [contextMessages]);

  const handleMessageClick = useCallback(async (uimsg: UIMessage) => {
    const parentId = uimsg.msg.parentMessageId ?? uimsg.msg.id;
    
    // 스레드 열기
    setActiveThreads(prev => {
      const next = new Set(prev);
      next.add(parentId);
      return next;
    });

    // 자식 메시지 지연 로드
    const parent = contextMessages.find(m => m.msg.id === parentId);
    if (parent && parent.children.length === 0 && (parent.replyCount ?? 0) > 0) {
      try {
        const children = await Fire.msg.getMessagesByParentIds(My.user.id, [parentId]);
        const sortedChildren = children.sort((a, b) => a.created < b.created ? -1 : 1).map(m => new UIMessage(m, []));

        const finalChildren: UIMessage[] = [];
        sortedChildren.forEach(c => {
          if (c.msg.emoticon && c.msg.replyToMessageId) {
            const replyParent = finalChildren.find(a => a.msg.id === c.msg.replyToMessageId);
            if (replyParent) replyParent.children.push(c);
            else finalChildren.push(c);
          } else {
            finalChildren.push(c);
          }
        });

        setContextMessages(prev => prev.map(m => {
          if (m.msg.id === parentId) {
            const updated = new UIMessage(m.msg, finalChildren);
            updated.isNewDay = m.isNewDay;
            return updated;
          }
          return m;
        }));
      } catch (e) {
        console.error('Failed to load thread children:', e);
      }
    }

    setCurrentMessage(uimsg);
  }, [contextMessages]);

  const onSendedMessage = useCallback((uimsg: UIMessage) => {
    const parentId = uimsg.msg.parentMessageId ?? uimsg.msg.id;
    setContextMessages(prev => prev.map(m => {
      if (m.msg.id === parentId) {
        return m.withChild(uimsg);
      }
      return m;
    }));
    setCurrentMessage(null);
  }, []);

  useEffect(() => {
    const handleCreate = (text: string) => {
      // 룸 페이지(채널) 안창이 아니면(검색페이지 등) MessageModal에서 직접 메모창을 띄워줌
      const pathname = window.location.pathname;
      const isRoomPageOpened = pathname.includes('/channel/') || pathname.includes('/room/');
      if (isRoomPageOpened) return; // 룸 페이지에서는 RoomPage 자체나 PanelMemos가 처리

      if (!room) return;
      const item = new RoomItem(room.id, My.user);
      item.type = 'memo';
      item.text = text?.replaceAll('\n', '<br/>');
      item.editableBy = 'everyone';
      item.id = ''; // 임시 ID (새 메모)
      setDirectMemoItem(item);
    };

    MemoModalEmitter.on('create', handleCreate);
    return () => {
      MemoModalEmitter.removeListener('create', handleCreate);
    };
  }, [room]);

  async function buildUIMessages(parentDocs: any[]) {
    const parents = parentDocs.map(p => ({ ...p }) as Message);
    const parentIds = parents.map(p => p.id);

    // 댓글 수 + 리액션 데이터를 병렬로 가져옴 (자식 문서 전체 로드 없이)
    const [replyCounts, reactionMsgs] = await Promise.all([
      parentIds.length > 0 ? Fire.msg.getReplyCountsBatch(My.user.id, parentIds) : new Map<string, number>(),
      parentIds.length > 0 ? Fire.msg.getReactionsByParentIds(My.user.id, parentIds) : [] as Message[],
    ]);

    const uiParents = parents.map(p => {
      const ui = new UIMessage(p, []);
      // 전체 자식 수에서 리액션(이모티콘) 수를 빼서 순수 댓글 수만 계산
      const totalCount = replyCounts.get(p.id) ?? 0;
      const reactionCount = reactionMsgs.filter(r => r.parentMessageId === p.id).length;
      ui.replyCount = Math.max(totalCount - reactionCount, 0);
      // 리액션을 msg.reactions 맵에 반영 (서버 데이터 보강)
      if (!p.reactions) {
        const msgReactions: { [emoticon: string]: string[] } = {};
        reactionMsgs.filter(r => r.parentMessageId === p.id && r.emoticon).forEach(r => {
          const key = r.emoticon as string;
          if (!msgReactions[key]) msgReactions[key] = [];
          const userId = r.user?.id;
          if (userId && !msgReactions[key].includes(userId)) {
            msgReactions[key].push(userId);
          }
        });
        if (Object.keys(msgReactions).length > 0) {
          ui.msg.reactions = msgReactions;
        }
      }
      return ui;
    });
    
    // 날짜 구분선 표기
    const sorted = uiParents.sort((a, b) => a.msg.created < b.msg.created ? -1 : 1);
    let lastDay = '';
    sorted.forEach(m => {
      const day = new Date(m.msg.created).toDateString();
      if (day !== lastDay) {
        m.isNewDay = true;
        lastDay = day;
      }
    });
    return sorted;
  }

  async function _fetchContext(baseMsg: Message, direction: 'older' | 'newer', pageSize: number) {
    const { collection, query, where, orderBy, getDocs, limit, startAfter } = await import("firebase/firestore");
    const store = Fire.store;
    const msgCol = collection(store, Message.COL);
    const orderDir = direction === 'older' ? 'desc' : 'asc';
    
    const dateStr = baseMsg.created instanceof Date ? baseMsg.created.toISOString() : baseMsg.created;
    
    const q = query(
      msgCol,
      where("roomId", "==", baseMsg.roomId),
      where("parentMessageId", "==", null),
      where("userIds", "array-contains", My.user.id),
      orderBy("created", orderDir),
      startAfter(dateStr),
      limit(pageSize)
    );
    
    const snap = await getDocs(q);
    let messages = snap.docs.map(d => d.data() as Message);
    
    if (direction === 'older') {
      messages.reverse();
    }
    
    return {
      messages,
      hasMore: snap.docs.length === pageSize
    };
  }

  async function initiate() {
    if (!messageToShow) return;
    setRoom(My.Rooms.get(messageToShow.roomId));
    setInProgress(true);
    
    try {
      const PAGE_SIZE = 5;
      
      let targetContextMsg: Message = messageToShow;
      if (messageToShow.parentMessageId) {
        const p = await Fire.msg.getMessage(messageToShow.parentMessageId);
        if (p) targetContextMsg = p;
      }
      
      const centerMessages = [targetContextMsg];
      
      // 데이터 패칭 + 최소 스켈레톤 표시 시간 병렬 실행
      const minDelay = new Promise(r => setTimeout(r, 300));
      const [olderResult, newerResult] = await Promise.all([
        _fetchContext(targetContextMsg, 'older', PAGE_SIZE),
        _fetchContext(targetContextMsg, 'newer', PAGE_SIZE),
        minDelay,
      ]);
      
      const allMessages = [...olderResult.messages, ...centerMessages, ...newerResult.messages];
      const uniqueParents = Array.from(new Map(allMessages.map(p => [p.id, p])).values());
      const uiMsgs = await buildUIMessages(uniqueParents);
      
      setContextMessages(uiMsgs);
      setHasOlder(olderResult.hasMore);
      setHasNewer(newerResult.hasMore);

      // 초기 펼침 대상의 자식 메시지 로드
      const expandedParentId = messageToShow.parentMessageId ?? messageToShow.id;
      const expandedParent = uiMsgs.find(m => m.msg.id === expandedParentId);
      if (expandedParent && expandedParent.children.length === 0 && (expandedParent.replyCount ?? 0) > 0) {
        try {
          const children = await Fire.msg.getMessagesByParentIds(My.user.id, [expandedParentId]);
          const sortedChildren = children.sort((a, b) => a.created < b.created ? -1 : 1).map(m => new UIMessage(m, []));
          const finalChildren: UIMessage[] = [];
          sortedChildren.forEach(c => {
            if (c.msg.emoticon && c.msg.replyToMessageId) {
              const replyParent = finalChildren.find(a => a.msg.id === c.msg.replyToMessageId);
              if (replyParent) replyParent.children.push(c);
              else finalChildren.push(c);
            } else {
              finalChildren.push(c);
            }
          });
          setContextMessages(prev => prev.map(m => {
            if (m.msg.id === expandedParentId) {
              const updated = new UIMessage(m.msg, finalChildren);
              updated.isNewDay = m.isNewDay;
              updated.replyCount = m.replyCount;
              return updated;
            }
            return m;
          }));
        } catch (e) {
          console.error('Failed to load initial thread children:', e);
        }
      }
      
      // 스크롤: 부모 메시지 기준으로 이동 (답글 클릭 시에도 부모 카드가 보이도록)
      const scrollTargetId = expandedParentId;
      setTimeout(() => {
        const targetEl = document.getElementById(`modal-msg-${scrollTargetId}`);
        if (targetEl && scrollRef.current) {
          targetEl.scrollIntoView({ behavior: 'auto', block: 'start' });
          targetEl.classList.add('bg-primary/10', 'transition-colors', 'duration-1000');
          setTimeout(() => targetEl.classList.remove('bg-primary/10'), 2000);
        }
      }, 400);
      
    } catch (e) {
      console.error("Native context fetch failed:", e);
    }
    setInProgress(false);
  }

  async function loadMore(direction: 'older' | 'newer') {
    if (!messageToShow || contextMessages.length === 0 || inProgress) return;
    setInProgress(true);
    
    const cursorMsg = direction === 'older' ? contextMessages[0].msg : contextMessages[contextMessages.length - 1].msg;
    const PAGE_SIZE = 20;

    let previousScrollHeight = 0;
    if (scrollRef.current && direction === 'older') {
      previousScrollHeight = scrollRef.current.scrollHeight;
    }

    try {
      const result = await _fetchContext(cursorMsg, direction, PAGE_SIZE);
      if (result.messages.length > 0) {
        const uiMsgs = await buildUIMessages(result.messages);
        if (direction === 'older') {
          setContextMessages(prev => {
            // 날짜 구분선 재계산 (새로 로드된 메시지의 마지막 날짜와 기존 첫 메시지의 날짜가 같으면 기존 첫 메시지의 isNewDay를 끈다)
            const merged = [...uiMsgs, ...prev];
            let lastDay = '';
            merged.forEach(m => {
              const day = new Date(m.msg.created).toDateString();
              m.isNewDay = day !== lastDay;
              lastDay = day;
            });
            return merged;
          });
          setHasOlder(result.hasMore);
          
          setTimeout(() => {
            if (scrollRef.current) {
              const newScrollHeight = scrollRef.current.scrollHeight;
              scrollRef.current.scrollTop += (newScrollHeight - previousScrollHeight);
            }
          }, 0);
        } else {
          setContextMessages(prev => {
            const merged = [...prev, ...uiMsgs];
            let lastDay = '';
            merged.forEach(m => {
              const day = new Date(m.msg.created).toDateString();
              m.isNewDay = day !== lastDay;
              lastDay = day;
            });
            return merged;
          });
          setHasNewer(result.hasMore);
        }
      } else {
        if (direction === 'older') setHasOlder(false);
        else setHasNewer(false);
      }
    } catch (e) {
      console.error("Load more context failed:", e);
    }
    setInProgress(false);
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (inProgress) return;

    if (scrollTop < 100 && hasOlder) {
      loadMore('older');
    } else if (scrollHeight - scrollTop - clientHeight < 100 && hasNewer) {
      loadMore('newer');
    }
  };

  function gotoRoom() {
    setShowModal(false);
    history.replace(`/channel/${room?.id}`);
    setMessageToShow(null);
  }

  function onOpenChange(open: boolean) {
    setShowModal(open);
    if (!open) setMessageToShow(null);
  }

  return (
    <Dialog open={showModal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85vh] p-0 flex flex-col bg-background">
        <DialogHeader className="px-6 py-4 border-b shrink-0 space-y-0">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={gotoRoom}
              className="text-sm font-medium hover:text-primary -ml-3"
            >
              {room ? Room.getRoomNameWithSharp(room, My.user.id) : <Trans>Loading...</Trans>}
            </Button>
          </div>
          {inProgress && (
            <div className="w-full h-1 bg-muted overflow-hidden rounded-full mt-2 absolute top-0 left-0">
              <div className="h-full w-full bg-primary animate-pulse" />
            </div>
          )}
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 flex flex-col relative" onScroll={handleScroll}>
          {/* 초기 로딩 스켈레톤 */}
          {contextMessages.length === 0 && inProgress && (
            <MessageSkeletonGroup />
          )}

          {/* 로딩 인디케이터 (상단 - 스크롤 추가 로드) */}
          {contextMessages.length > 0 && hasOlder && inProgress && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            </div>
          )}
          
          {contextMessages.length === 0 && !inProgress && (
            <div className="flex flex-col items-center justify-center p-10 text-muted-foreground opacity-70">
              <Trans>No messages could be loaded.</Trans>
            </div>
          )}

          {contextMessages.map((m) => {
            const isTarget = m.msg.id === messageToShow?.id;
            const isAutoMessage = !!m.msg.autoMessage;
            const replies = m.nonReactions();
            // children이 로드된 경우 실제 수, 아니면 UIMessage.replyCount(서버 카운트) 사용
            const replyCount = replies.length > 0 ? replies.length : (m.replyCount ?? m.msg.replyCount ?? 0);
            const isThreadExpanded = activeThreads.has(m.msg.id);

            return (
              <div key={m.msg.id} className="message-root px-4" id={`modal-msg-${m.msg.id}`}>
                {/* ── 날짜 구분선 (RoomMessageItem 동일) ── */}
                {m.isNewDay && (
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-meta font-bold leading-[20px] text-muted-foreground whitespace-nowrap tracking-tight">
                      {UtilApp.dateStrToDay(m.msg.created, My.user.lang)}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}

                {/* ── 메시지 카드 (RoomMessageItem 동일 구조) ── */}
                <div
                  className={cn(
                    "flex flex-col scroll-m-4 transition-all relative rounded-panel overflow-clip mb-2",
                    isAutoMessage
                      ? "py-0 px-0"
                      : "border",
                    !isAutoMessage && isTarget
                      ? "bg-[rgb(var(--msg-selected-bg))] border-[color:var(--msg-selected-border)]"
                      : !isAutoMessage ? "bg-[rgb(var(--message-bubble))] border-border" : ""
                  )}
                  role="article"
                  data-message-id={m.msg.id}
                >
                  {/* ── 부모 메시지 그룹 ── */}
                  <div
                    className={cn(
                      "flex flex-col transition-all",
                      isAutoMessage
                        ? ""
                        : "py-3.5 px-4 rounded-panel",
                    )}
                  >
                    <div className="message-box rounded-control transition-colors">
                      <MessageItem
                        m={m}
                        state='old'
                        onMessageClick={handleMessageClick}
                        roomUsers={room?.users}
                      />

                      {/* Bottom bar: 쓰레드 버튼 + 리액션 요약 (RoomMessageItem 동일) */}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {/* 쓰레드 버튼 */}
                        {replyCount > 0 && (
                          <button
                            className="inline-flex items-center justify-center gap-1 h-7 min-w-11 px-2 bg-background border border-border rounded-md text-body text-muted-foreground hover:bg-accent hover:border-border transition-colors shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleThread(m.msg.id);
                            }}
                          >
                            <span>{t`Replies`} {replyCount > 0 ? replyCount : ''}</span>
                            {isThreadExpanded ? (
                              <ChevronUp className="w-3 h-3 text-muted-foreground" />
                            ) : (
                              <IconChevronDown className="w-3 h-3 text-muted-foreground" />
                            )}
                          </button>
                        )}

                        {/* 리액션 버튼 */}
                        {!m.msg.autoMessage && (
                          <Popover open={activeReactionId === m.msg.id} onOpenChange={(open) => setActiveReactionId(open ? m.msg.id : null)} modal>
                            <PopoverTrigger asChild>
                              <Button
                                variant="icon-reaction"
                                data-state={activeReactionId === m.msg.id ? "on" : undefined}
                              >
                                <IconSmilePlus />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-3 rounded-xl w-auto z-[80]"
                              side="right"
                              align="start"
                              sideOffset={4}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MsgInputFooterReactions room={room!} parentMessage={m} onReactionChildrenChange={() => {}} onReactionUpdate={() => {
                                // 리렌더링 유발
                                setContextMessages(prev => [...prev]);
                                setActiveReactionId(null);
                              }} />
                            </PopoverContent>
                          </Popover>
                        )}
                        {/* 리액션 요약: 자식 로드 전에는 서버 reactions 맵 사용 */}
                        {m.reactions().length > 0
                          ? <GetReactionSummary emotes={m.reactions()} roomUsers={room?.users} />
                          : <ServerReactionSummary reactions={m.msg.reactions} />
                        }
                      </div>
                    </div>
                  </div>

                  {/* ── 자식 메시지 그룹 (Expanded Thread) ── */}
                  {isThreadExpanded && (
                    <div className="pt-3 pl-8 pr-4 pb-2 w-full">
                      {replies.map((reply) => (
                        <div key={reply.msg.id} className="mb-0">
                          <div className="relative rounded-xl transition-colors p-2">
                            {/* ← 답글 화살표 (↩) - Figma */}
                            <div className="flex gap-2">
                              <CornerDownRight className="w-4 h-4 text-muted-foreground shrink-0 mt-3" />
                              <div className="flex-1 min-w-0">
                                <MessageItem
                                  m={reply}
                                  state='old'
                                  onMessageClick={handleMessageClick}
                                  roomUsers={room?.users}
                                />
                                {/* 답글 리액션 행 */}
                                {!reply.msg.autoMessage && (
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    {/* 리액션 버튼 */}
                                    <Popover open={activeReactionId === reply.msg.id} onOpenChange={(open) => setActiveReactionId(open ? reply.msg.id : null)} modal>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="icon-reaction"
                                          data-state={activeReactionId === reply.msg.id ? "on" : undefined}
                                        >
                                          <IconSmilePlus />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="p-3 rounded-xl w-auto z-[80]"
                                        side="right"
                                        align="start"
                                        sideOffset={4}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MsgInputFooterReactions room={room!} parentMessage={reply} onReactionChildrenChange={() => {}} onReactionUpdate={() => {
                                          setContextMessages(prev => [...prev]);
                                          setActiveReactionId(null);
                                        }} />
                                      </PopoverContent>
                                    </Popover>
                                    <GetReactionSummary emotes={reply.reactions()} roomUsers={room?.users} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}


                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* 로딩 인디케이터 (하단 - 스크롤 추가 로드) */}
          {contextMessages.length > 0 && hasNewer && inProgress && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            </div>
          )}
        </div>
        
        {/* 공통 입력창 (채널 입력창과 동일) */}
        {room && currentMessage && (
          <div className="shrink-0 border-t border-border bg-background shadow-[0_-4px_10px_rgba(0,0,0,0.03)] z-20">
            <MsgInputFooter
              room={room}
              parentMessage={currentMessage}
              messages={contextMessages}
              onClearParent={() => setCurrentMessage(null)}
              onProgressForFile={(id, pf) => {}}
              onSendedMessage={onSendedMessage}
              soribotsRef={soribotsRef}
            />
          </div>
        )}
      </DialogContent>
      {directMemoItem && (
        <MemoOpenModal item={directMemoItem} addText={null} onClose={() => setDirectMemoItem(null)} />
      )}
    </Dialog>
  );
};

export default MessageModal;
